import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {Object} ImportStudentsResponse
 * @property {boolean} status - Whether the import was queued successfully
 * @property {number} import_log_id - The ID of the import log
 * @property {string} message - Status message from the server
 */

/**
 * Custom hook to import students via file upload for a school.
 * Accepts a file (e.g. CSV/Excel), sends it as FormData, and returns
 * the import job status.
 */
export const useImportStudents = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { axiosInstance } = useAxios();

  /**
   * Import students by uploading a file
   * @async
   * @param {File} file - The file to import (CSV/Excel)
   * @param {() => void} [onSuccess] - Optional callback on success
   * @returns {Promise<ImportStudentsResponse|undefined>}
   */
  const importStudents = async (file, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);

      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/import/students`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setSuccess(true);

      if (onSuccess) {
        onSuccess(
          "Your import has been started. Once imported, we will send the email to you.",
        );
      }

      return response?.data;
    } catch (err) {
      setSuccess(false);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    importStudents,
    loading,
    error,
    success,
  };
};

